import React from "react";
import { ShieldCheck, Leaf, Stethoscope, Award, CircleCheck, Microscope, Feather, Droplets } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SafetyQualityStandards() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const trustIndicators = [{
    icon: Stethoscope,
    label: "Medical Grade"
  }, {
    icon: Droplets,
    label: "BPA Free & Safe"
  }, {
    icon: ShieldCheck,
    label: "Discreet Delivery"
  }, {
    icon: Award,
    label: "ISO Certified"
  }];
  const certifications = [{
    label: "Clinically Validated",
    color: "bg-primary/10 text-primary border-primary/30"
  }, {
    label: "BPA & Phthalate Free",
    color: "bg-primary/10 text-primary border-primary/30"
  }, {
    label: "FDA Registered Facility",
    color: "bg-primary/10 text-primary border-primary/30"
  }, {
    label: "Medical Grade Silicone",
    color: "bg-primary/10 text-primary border-primary/30"
  }, {
    label: "Dermatologist Tested",
    color: "bg-primary/10 text-primary border-primary/30"
  }, {
    label: "Eco-Conscious Sourcing",
    color: "bg-primary/10 text-primary border-primary/30"
  }];
  return <section data-section-id="9073" id="quality-you-can-trust" ref={ref} className={`bg-muted text-foreground py-20 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {}
      <div className="absolute inset-0 opacity-5 pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592322/img/parrot_feather_macro_texture.png)] bg-cover bg-center mix-blend-overlay"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        {}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20 border-y border-border py-8 bg-white shadow-sm rounded-lg">
          {trustIndicators.map((item, i) => <div key={i} data-index={i} className="flex flex-col items-center justify-center text-center gap-3 group">
              <div className="p-4 rounded-full bg-accent text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 shadow-sm">
                <item.icon className="w-6 h-6" />
              </div>
              <span className="font-bold tracking-tight text-sm md:text-base text-muted-foreground group-hover:text-primary transition-colors">
                {item.label}
              </span>
            </div>)}
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-start">
          
          {}
          <div className="relative space-y-8">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-border group">
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/20 to-transparent z-10"></div>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592322/img/macaw_feather_detail_macro.png" alt="Macro detail of healthcare product materials showing precision and cleanliness" className="w-full h-[500px] object-cover transform group-hover:scale-105 transition-transform duration-700" />
              
              <div className="absolute bottom-0 left-0 right-0 p-8 z-20">
                <h3 className="text-2xl font-sans font-bold mb-2 text-white">okfun Professional Standards</h3>
                <p className="text-white/90 mb-6 max-w-md font-medium">
                  Every okfun product undergoes rigorous clinical validation to ensure it meets the highest standards of medical precision and personal safety.
                </p>
                
                <div className="flex flex-wrap gap-3">
                  {certifications.map((cert, i) => <Badge key={i} data-index={i} variant="outline" className={`backdrop-blur-md px-3 py-1.5 text-xs font-bold tracking-wider uppercase ${cert.color}`}>
                      <CircleCheck className="w-3 h-3 mr-1.5" />
                      {cert.label}
                    </Badge>)}
                </div>
              </div>
            </div>

            {}
            <div className="hidden lg:block absolute -top-10 -right-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
            <div className="hidden lg:block absolute -bottom-10 -left-10 w-40 h-40 bg-accent/30 rounded-full blur-3xl"></div>
          </div>

          {}
          <div className="flex flex-col justify-center h-full">
            <div className="mb-8">
              <h2 className="text-3xl md:text-5xl font-sans font-bold mb-4 leading-tight tracking-tight">
                Quality You Can <span className="text-primary">Always Trust</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed font-medium">
                We believe high-quality health tools should be accessible, safe, and designed for your modern lifestyle. Our commitment starts at the molecular level.
              </p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4" defaultValue="item-1">
              <AccordionItem value="item-1" className="border border-border rounded-xl bg-white px-4 data-[state=open]:bg-accent/50 transition-colors shadow-sm">
                <AccordionTrigger className="hover:no-underline py-5 text-lg font-bold text-secondary">
                  <div className="flex items-center gap-4">
                    <Droplets className="w-5 h-5 text-primary" />
                    BPA Free Materials
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5 pl-9 font-medium">
                  We strictly ensure all okfun baby care and intimate wellness products are free from BPA, phthalates, and harmful chemicals, using only medical-grade materials that are safe for your most sensitive needs.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border border-border rounded-xl bg-white px-4 data-[state=open]:bg-accent/50 transition-colors shadow-sm">
                <AccordionTrigger className="hover:no-underline py-5 text-lg font-bold text-secondary">
                  <div className="flex items-center gap-4">
                    <Microscope className="w-5 h-5 text-primary" />
                    Clinical Grade Accuracy
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5 pl-9 font-medium">
                  From okfun thermometers to blood pressure monitors, every medical device is tested against professional healthcare benchmarks. We manually inspect structural integrity to ensure reliable performance.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border border-border rounded-xl bg-white px-4 data-[state=open]:bg-accent/50 transition-colors shadow-sm">
                <AccordionTrigger className="hover:no-underline py-5 text-lg font-bold text-secondary">
                  <div className="flex items-center gap-4">
                    <ShieldCheck className="w-5 h-5 text-primary" />
                    Discreet & Thoughtful
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5 pl-9 font-medium">
                  We respect your privacy. All okfun orders are shipped in neutral, unmarked packaging, while our product designs blend seamlessly into your modern home environment for a sophisticated aesthetic.
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="mt-10">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-10 rounded-full shadow-lg hover:scale-105 transition-all">
                <Link to="/ProfessionalMedicalPage">
                  Explore Professional Medical
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}